import React, { useState, useEffect } from 'react';
import { X, Trophy, Target, Rocket, TrendingUp, Crown, Flame, Star, Zap } from 'lucide-react';

const POPUP_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds
const POPUP_STORAGE_KEY = 'lastMotivationalPopup';

const MotivationalPopup = ({ user, stats, leaderboardData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [motivationData, setMotivationData] = useState(null);

  useEffect(() => {
    // Check if we should show the popup
    const checkAndShowPopup = () => {
      const lastShown = localStorage.getItem(POPUP_STORAGE_KEY);
      const now = Date.now();
      
      // Show popup if never shown or more than 1 hour has passed
      if (!lastShown || (now - parseInt(lastShown)) >= POPUP_INTERVAL) {
        // Generate motivation based on stats
        if (stats || leaderboardData) {
          const motivation = generateMotivation(user, stats, leaderboardData);
          if (motivation) {
            setMotivationData(motivation);
            setIsVisible(true);
            localStorage.setItem(POPUP_STORAGE_KEY, now.toString());
          }
        }
      }
    };

    // Initial check after a short delay (let data load)
    const timer = setTimeout(checkAndShowPopup, 3000);
    
    return () => clearTimeout(timer);
  }, [user, stats, leaderboardData]);

  const generateMotivation = (user, stats, leaderboardData) => {
    const motivations = [];
    const userName = user?.agent_name?.split(' ')[0] || 'Champion';

    // Time-based greeting using EST timezone
    const estTime = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false });
    const currentHour = parseInt(estTime, 10);
    let greeting = '';
    if (currentHour < 12) greeting = 'Good Morning';
    else if (currentHour < 17) greeting = 'Good Afternoon';
    else greeting = 'Good Evening';

    // Stats-based motivation
    if (stats) {
      const growth = stats.growth_percentage || 0;
      const currentWeekTotal = stats.current_week?.total || 0;
      const lastWeekTotal = stats.last_week?.total || 0;
      const dailyAvg = stats.daily_average || 0;
      const daysElapsed = stats.current_week?.days_elapsed || 0;
      const prediction = stats.prediction?.total || 0;

      // Exceptional growth
      if (growth > 50) {
        motivations.push({
          type: 'achievement',
          icon: Flame,
          iconColor: 'text-orange-500',
          bgGradient: 'from-orange-500 to-red-500',
          title: `${greeting}, ${userName}!`,
          subtitle: 'You\'re ON FIRE!',
          message: `Your ${growth}% growth this week is exceptional! You're setting new standards for the team.`,
          actionText: 'Keep Crushing It!'
        });
      }
      // Good growth
      else if (growth > 20) {
        motivations.push({
          type: 'growth',
          icon: TrendingUp,
          iconColor: 'text-emerald-500',
          bgGradient: 'from-emerald-500 to-teal-500',
          title: `${greeting}, ${userName}!`,
          subtitle: 'Strong Performance!',
          message: `You're up ${growth}% from last week! Your hard work is paying off. Keep pushing forward!`,
          actionText: 'Continue the Streak!'
        });
      }
      // Steady performance
      else if (growth >= 0 && currentWeekTotal > 0) {
        motivations.push({
          type: 'steady',
          icon: Target,
          iconColor: 'text-blue-500',
          bgGradient: 'from-blue-500 to-indigo-500',
          title: `${greeting}, ${userName}!`,
          subtitle: 'Stay Focused!',
          message: `You've made ${currentWeekTotal} sales in ${daysElapsed} days. Prediction shows ${prediction} by end of week. You've got this!`,
          actionText: 'Let\'s Do This!'
        });
      }
      // Need a boost
      else if (growth < 0) {
        motivations.push({
          type: 'boost',
          icon: Rocket,
          iconColor: 'text-purple-500',
          bgGradient: 'from-purple-500 to-pink-500',
          title: `${greeting}, ${userName}!`,
          subtitle: 'Time to Rise!',
          message: `Every champion has tough days. Yesterday is history. Today is your chance to turn things around!`,
          actionText: 'I\'m Ready!'
        });
      }

      // Daily average motivation
      if (dailyAvg > 0 && daysElapsed < 5) {
        motivations.push({
          type: 'daily',
          icon: Zap,
          iconColor: 'text-amber-500',
          bgGradient: 'from-amber-500 to-orange-500',
          title: `${greeting}, ${userName}!`,
          subtitle: `${5 - daysElapsed} Days Left This Week!`,
          message: `At ${dailyAvg} sales/day, you're on track for ${prediction} this week. Push a little harder and break your record!`,
          actionText: 'Challenge Accepted!'
        });
      }
    }

    // Leaderboard-based motivation
    if (leaderboardData?.leaderboard) {
      const sortedLeaderboard = [...leaderboardData.leaderboard].sort((a, b) => b.current_month - a.current_month);
      const userRank = sortedLeaderboard.findIndex(a => a.email === user?.email) + 1;
      const totalAgents = sortedLeaderboard.length;
      
      if (userRank > 0) {
        // Top performer
        if (userRank === 1) {
          motivations.push({
            type: 'champion',
            icon: Crown,
            iconColor: 'text-amber-500',
            bgGradient: 'from-amber-400 to-yellow-500',
            title: `${greeting}, Champion!`,
            subtitle: 'You\'re #1!',
            message: `You're leading the pack! Stay focused and maintain your position at the top. The team is watching!`,
            actionText: 'Defend My Title!'
          });
        }
        // Top 3
        else if (userRank <= 3) {
          const leader = sortedLeaderboard[0];
          const gap = leader.current_month - (sortedLeaderboard[userRank - 1]?.current_month || 0);
          motivations.push({
            type: 'podium',
            icon: Trophy,
            iconColor: 'text-amber-500',
            bgGradient: 'from-blue-600 to-indigo-600',
            title: `${greeting}, ${userName}!`,
            subtitle: `You're in the Top 3!`,
            message: `Only ${gap} sales away from ${leader.agent_name}'s top spot. This month could be your month!`,
            actionText: 'Go for Gold!'
          });
        }
        // Close to top 3
        else if (userRank <= 5) {
          const thirdPlace = sortedLeaderboard[2];
          const gap = thirdPlace.current_month - (sortedLeaderboard[userRank - 1]?.current_month || 0);
          motivations.push({
            type: 'rising',
            icon: Star,
            iconColor: 'text-purple-500',
            bgGradient: 'from-purple-600 to-pink-600',
            title: `${greeting}, ${userName}!`,
            subtitle: 'The Podium is Close!',
            message: `You're rank #${userRank}! Just ${gap} more sales to break into the Top 3. Make it happen!`,
            actionText: 'I\'m Coming for It!'
          });
        }
        // General motivation
        else {
          const aheadAgent = sortedLeaderboard[userRank - 2];
          const gap = aheadAgent ? aheadAgent.current_month - (sortedLeaderboard[userRank - 1]?.current_month || 0) : 0;
          motivations.push({
            type: 'climb',
            icon: Target,
            iconColor: 'text-cyan-500',
            bgGradient: 'from-cyan-500 to-blue-500',
            title: `${greeting}, ${userName}!`,
            subtitle: 'Keep Climbing!',
            message: gap > 0 
              ? `You're #${userRank}. Only ${gap} sales to overtake ${aheadAgent?.agent_name} and move up! Every sale counts!`
              : `You're #${userRank} out of ${totalAgents}. Every day is a new opportunity to climb higher!`,
            actionText: 'Let\'s Move Up!'
          });
        }
      }
    }

    // Return a random motivation from available options
    if (motivations.length > 0) {
      return motivations[Math.floor(Math.random() * motivations.length)];
    }

    // Default motivation if no stats available
    return {
      type: 'default',
      icon: Star,
      iconColor: 'text-blue-500',
      bgGradient: 'from-blue-500 to-indigo-500',
      title: `${greeting}, ${userName}!`,
      subtitle: 'Make Today Count!',
      message: 'Success is built one day at a time. Focus on giving your best today, and the results will follow!',
      actionText: 'Let\'s Go!'
    };
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible || !motivationData) return null;

  const IconComponent = motivationData.icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fadeIn">
      <div className="w-full max-w-md transform animate-scaleIn">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${motivationData.bgGradient} p-6 pb-16 relative`}>
            {/* Close button */}
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            {/* Floating icon */}
            <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
              <div className="w-20 h-20 bg-white rounded-2xl shadow-xl flex items-center justify-center transform rotate-3">
                <IconComponent className={`w-10 h-10 ${motivationData.iconColor}`} />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="pt-14 pb-6 px-6 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-1">
              {motivationData.title}
            </h2>
            <p className={`text-lg font-semibold bg-gradient-to-r ${motivationData.bgGradient} bg-clip-text text-transparent mb-4`}>
              {motivationData.subtitle}
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              {motivationData.message}
            </p>
            
            {/* Action Button */}
            <button
              onClick={handleClose}
              className={`w-full py-3.5 px-6 bg-gradient-to-r ${motivationData.bgGradient} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all`}
            >
              {motivationData.actionText}
            </button>
          </div>

          {/* Decorative elements */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
              <Zap className="w-4 h-4" />
              <span>Creative Infotech - Excellence in Action</span>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default MotivationalPopup;
